import React, { useState, useEffect } from 'react';

import { getInitialsFromName } from '@trqk/utils';
import {
    GradientDot,
    IconCamera,
    LazyImage,
    LoadingEllipses,
} from '@trqk/components';
import { useGetTrqkUserAvatarLazyQuery } from '@trqk/api';

import { useModal } from '../../../../../hooks';
import { ChangeAvatarImageModal } from './ChangeAvatarImageModal';

interface AccountAvatarProps {
    userId: Maybe<string>;
}

const width = 168;

const defaultUserName = 'user';

/**
 * Displays a round profile image for a user from a given imageUrl. Gives the user the ability
 * to update the image by uploading a new image file to the given S3 bucket specified by the
 * imageBucketName.
 */
export const AccountAvatar = ({ userId }: AccountAvatarProps): JSX.Element => {
    const modal = useModal();
    const [getUser, getUserQuery] = useGetTrqkUserAvatarLazyQuery();

    useEffect(() => {
        if (userId) {
            getUser({
                variables: {
                    id: userId,
                },
            });
        }
    }, [userId]);

    const name = getUserQuery?.data?.getTrqkUser?.name ?? defaultUserName;
    const avatarUrl = getUserQuery?.data?.getTrqkUser?.avatar_url ?? null;
    const [loading, setLoading] = useState(false);
    const [imageHash, setImageHash] = useState(Date.now());
    const showGradientDot =
        getUserQuery.called && !getUserQuery.loading && avatarUrl === null;
    const showImage = avatarUrl !== null;

    return (
        <div
            className="absolute"
            style={{
                width,
                height: width,
            }}
        >
            {showGradientDot && (
                <GradientDot gradientColor="primary" width={width}>
                    <div className="text-white text-4xl text-center">
                        {name !== defaultUserName && (
                            <>{getInitialsFromName(name)}</>
                        )}
                    </div>
                </GradientDot>
            )}
            {!showGradientDot && !showImage && (
                // Placeholder to maintain layout before image loads
                <div
                    className="rounded-full"
                    style={{
                        width,
                        height: width,
                    }}
                />
            )}
            {showImage && (
                <LazyImage
                    src={`${avatarUrl}?${imageHash}`}
                    alt={`Avatar for ${name}`}
                    className="rounded-full object-cover"
                    style={{
                        width,
                        height: width,
                    }}
                />
            )}
            <div
                className="relative rounded-full bg-primary-500 flex justify-center items-center"
                style={{ top: -50, left: 120, width: 44, height: 44 }}
            >
                <button
                    type="button"
                    onClick={(): void => {
                        if (userId) {
                            modal.add({
                                Content: ChangeAvatarImageModal,
                                input: {
                                    avatarUrl,
                                    setImageHash,
                                    setLoading,
                                    userId,
                                },
                            });
                        }
                    }}
                >
                    <IconCamera width={30} fillColor="white" />
                </button>
            </div>
            {loading && (
                <div className="relative" style={{ top: -140, left: 60 }}>
                    <LoadingEllipses width={50} fillColor="primary-500" />
                </div>
            )}
        </div>
    );
};
